import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import client, { API_BASE_URL } from '../api/client';
import { unwrapDateLike, safeDateString, dateMs } from '../utils/dateUtils';
import './DrillDownModal.css';

// Canonicalize a numeric facility/user id from any of its on-the-wire shapes
// ("1706", "1706.0", 1706, 1706.0). Returns null for null/empty/NaN.
const canonicalId = (raw) => {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw));
  return Number.isNaN(n) ? null : Math.round(n).toString();
};

// REFERRAL FIELD NAMES — see REFERRAL-FIELD-NAMING.md. The canonical role
// fields produced by DataCacheService are:
//   sending_*   = the actual sender (referring physician)
//   creating_*  = the typist
//   receiving_* = the destination
const DEFAULT_FIELDS = {
  referrals: [
    { key: 'created_date', label: 'Date' },
    { key: 'sending_facility', label: 'Sending Facility' },
    { key: 'receiving_facility', label: 'Receiving Facility' },
    { key: 'referral_type', label: 'Type' },
    { key: 'referral_for', label: 'Referral For' },
    { key: 'patient_first_name', label: 'Patient First' },
    { key: 'patient_last_name', label: 'Patient Last' },
    { key: 'sending_user_name', label: 'Sending Provider' },
    { key: 'receiving_user_name', label: 'Receiving Provider' },
    { key: 'creating_user_name', label: 'Creating Provider (typist)' },
    { key: 'receiving_speciality', label: 'Specialty' },
  ],
  vitalrounds: [
    { key: 'created_date', label: 'Date' },
    { key: 'sending_facility', label: 'Sending Facility' },
    { key: 'receiving_facility', label: 'Receiving Facility' },
    { key: 'referral_for', label: 'Referral For' },
    { key: 'patient_first_name', label: 'Patient First' },
    { key: 'patient_last_name', label: 'Patient Last' },
    { key: 'sending_user_name', label: 'Sending Provider' },
    { key: 'receiving_user_name', label: 'Receiving Provider' },
    { key: 'creating_user_name', label: 'Creating Provider (typist)' },
  ],
  messages: [
    { key: 'created_date', label: 'Date' },
    { key: 'facility_name', label: 'Facility' },
    { key: 'user_display_name', label: 'User' },
    { key: 'referral_messages_sent', label: 'Referral' },
    { key: 'vmail_messages_Sent', label: 'VMail' },
    { key: 'vchat_messages_Sent', label: 'VChat Sent' },
    { key: 'vchat_messages_received', label: 'VChat Recv' },
    { key: 'vround_messages_sent', label: 'VRounds' },
    { key: 'vtransfer_messages_sent', label: 'VTransfer' },
    { key: 'Other_messages_sent', label: 'Other' },
  ],
  logins: [
    { key: 'created_date', label: 'Date' },
    { key: 'facility_name', label: 'Facility' },
    { key: 'user_display_name', label: 'User' },
    { key: 'speciality', label: 'Specialty' },
    { key: 'web_login', label: 'Web' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'desktop_login', label: 'Desktop' },
  ],
  // 'groups' (rpt_consolidated_group_usage) was retired 2026-05; see backend
  // DataTypeNames for rationale.
  studies: [
    { key: 'created_date', label: 'Date' },
    { key: 'facility_name', label: 'Facility' },
    { key: 'modality', label: 'Modality' },
    { key: 'study_description', label: 'Study Description' },
    { key: 'process_type', label: 'Process' },
    { key: 'study_sent', label: 'Sent' },
    { key: 'study_received', label: 'Received' },
  ],
  // V2.1 endpoints. `facility_name`, `recipients_summary`, and
  // `recipient_user_ids` for `faxes` are SYNTHETIC fields produced by the
  // enrichment effect below — they don't exist on the raw cached record,
  // so toggling them on for a non-faxes type would render as "—".
  faxes: [
    { key: 'created_date', label: 'Date' },
    { key: 'facility_name', label: 'Facility' },
    { key: 'fax_status', label: 'Status' },
    { key: 'num_pages', label: 'Pages' },
    { key: 'recipients_summary', label: 'Recipients' },
    { key: 'group_id', label: 'Group ID' },
    { key: 'fax_id', label: 'Fax ID' },
  ],
  'fax-recipients': [
    { key: 'created_date', label: 'Date' },
    { key: 'fax_id', label: 'Fax ID' },
    { key: 'user_id', label: 'User ID' },
    { key: 'is_read', label: 'Read?' },
  ],
  'group-referrals': [
    { key: 'created_date', label: 'Date' },
    { key: 'referral_number', label: '#' },
    { key: 'referral_for', label: 'For' },
    { key: 'receiving_group_name', label: 'Group' },
    { key: 'is_referral_wizard', label: 'Wizard?' },
    { key: 'referral_status', label: 'Status' },
    { key: 'referral_first_msg_created', label: 'First Msg' },
  ],
  'study-details': [
    { key: 'created_at', label: 'Date' },
    { key: 'facility_name', label: 'Facility' },
    { key: 'modalities', label: 'Modality' },
    { key: 'study_description', label: 'Description' },
    { key: 'process_type', label: 'Process' },
    { key: 'study_id', label: 'Study ID' },
  ],
};

function DrillDownModal({
  records,
  dataType,
  binLabel,
  onClose,
  focusField = null,
  focusLabel = '',
  focusSum = null,
  chartPointValue = null,
  periodRowCount = null,
}) {
  const [sortCol, setSortCol] = useState(focusField);
  const [sortDir, setSortDir] = useState(focusField ? 'desc' : 'asc');
  const [showConfig, setShowConfig] = useState(false);
  const [savedColumns, setSavedColumns] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [configDraft, setConfigDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  // Enrichment data joined into records on the fly. Map<canonical_id, name|payload>.
  // facilityMap covers any data type with a numeric facility_id; faxRecipientsByFax
  // is fax-specific. Both are populated lazily by the effect below.
  const [enrichments, setEnrichments] = useState({ facilityMap: null, faxRecipientsByFax: null });
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [configRes, fieldsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/cache/drilldown-config/${dataType}`),
          axios.get(`${API_BASE_URL}/api/cache/drilldown-fields/${dataType}`)
        ]);
        if (cancelled) return;
        if (configRes.data?.columns) {
          setSavedColumns(configRes.data.columns);
        }
        if (fieldsRes.data?.fields) {
          setAvailableFields(fieldsRes.data.fields);
        }
      } catch (err) {
        console.error('Error loading drilldown config:', err);
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [dataType]);

  useEffect(() => {
    if (focusField) {
      setSortCol(focusField);
      setSortDir('desc');
    }
  }, [focusField, binLabel]);

  // Enrichment: facility-id → name lookup (always when records carry a
  // facility_id), and faxes-only fax-recipients join. The recipients call
  // is scoped to ±1 day around the displayed records' date range so we
  // don't drag in the full ~600k recipient cache when we only need
  // ~200 rows. Skipped entirely for data types that don't need it.
  useEffect(() => {
    if (!records || records.length === 0) return;
    const needsFacility = dataType === 'faxes' || dataType === 'study-details';
    const needsFaxRecipients = dataType === 'faxes';
    if (!needsFacility && !needsFaxRecipients) return;

    let cancelled = false;
    setEnrichmentLoading(true);

    async function enrich() {
      try {
        const tasks = [];

        // Facility lookup — same fetch is used for any data type that has a
        // numeric facility_id we need to humanize.
        if (needsFacility) {
          tasks.push(client.get('/api/cache/facilities').then(r => ({ kind: 'facilities', body: r.data })));
        }

        // Fax-recipients window. Use the records' own min/max timestamps
        // padded by ±1 day; this keeps the payload small while tolerating
        // recipient rows whose created_date drifted slightly from the
        // parent fax's.
        if (needsFaxRecipients) {
          // dateMs unwraps Salesforce-shaped date objects so the window
          // calc isn't silently eaten when a record carries one.
          const stamps = records
            .map(r => dateMs(r.timestamp || (r.data || r.Data || {}).created_date))
            .filter(t => t > 0);
          if (stamps.length > 0) {
            const min = new Date(Math.min(...stamps));
            const max = new Date(Math.max(...stamps));
            min.setDate(min.getDate() - 1);
            max.setDate(max.getDate() + 1);
            const fmt = d => d.toISOString().slice(0, 10);
            tasks.push(
              client.get('/api/cache/data', {
                params: { type: 'fax-recipients', startDate: fmt(min), endDate: fmt(max) }
              }).then(r => ({ kind: 'fax-recipients', body: r.data }))
            );
          }
        }

        const results = await Promise.all(tasks);
        if (cancelled) return;

        const next = { facilityMap: null, faxRecipientsByFax: null };

        for (const { kind, body } of results) {
          if (kind === 'facilities' && body?.facilities) {
            const map = new Map();
            for (const f of body.facilities) {
              const id = canonicalId(f.vitalEngine_Facility_ID__c);
              if (id) map.set(id, f.name);
            }
            next.facilityMap = map;
          }
          if (kind === 'fax-recipients' && body?.records) {
            const map = new Map();
            for (const r of body.records) {
              const d = r.data || r.Data || {};
              const faxId = canonicalId(d.fax_id);
              if (!faxId) continue;
              if (!map.has(faxId)) map.set(faxId, []);
              map.get(faxId).push({
                user_id: canonicalId(d.user_id),
                is_read: Number(d.is_read) === 1,
              });
            }
            next.faxRecipientsByFax = map;
          }
        }

        setEnrichments(next);
      } catch (err) {
        // Enrichment is best-effort — if it fails, the modal still works,
        // just without the joined columns.
        console.error('Drill-down enrichment failed:', err);
      } finally {
        if (!cancelled) setEnrichmentLoading(false);
      }
    }
    enrich();
    return () => { cancelled = true; };
  }, [dataType, records]);

  // Merge the synthetic enrichment fields onto each record's data so the
  // existing column-rendering machinery can address them by string key
  // exactly like real upstream fields.
  const enrichedRecords = useMemo(() => {
    if (!records) return [];
    if (!enrichments.facilityMap && !enrichments.faxRecipientsByFax) return records;

    return records.map(r => {
      const data = r.data || r.Data || {};
      const synthetic = {};

      if (enrichments.facilityMap) {
        const facId = canonicalId(data.facility_id);
        const name = facId ? enrichments.facilityMap.get(facId) : null;
        // Don't clobber an upstream-provided facility_name (messages/logins
        // already populate it from VitalEngine).
        if (name && !data.facility_name) {
          synthetic.facility_name = name;
        }
      }

      if (dataType === 'faxes' && enrichments.faxRecipientsByFax) {
        const faxId = canonicalId(data.fax_id);
        const recips = (faxId && enrichments.faxRecipientsByFax.get(faxId)) || [];
        if (recips.length > 0) {
          const readCount = recips.filter(x => x.is_read).length;
          synthetic.recipients_summary = `${readCount} of ${recips.length} read`;
          synthetic.recipients_count = recips.length;
          synthetic.recipients_read_count = readCount;
          synthetic.recipient_user_ids = recips.map(x => x.user_id).filter(Boolean).join(', ');
        } else {
          synthetic.recipients_summary = '—';
          synthetic.recipients_count = 0;
          synthetic.recipients_read_count = 0;
          synthetic.recipient_user_ids = '';
        }
      }

      return { ...r, data: { ...data, ...synthetic } };
    });
  }, [records, dataType, enrichments]);

  const columns = useMemo(() => {
    if (savedColumns && savedColumns.length > 0) return savedColumns;
    const defaults = DEFAULT_FIELDS[dataType];
    if (defaults) return defaults;
    if (!enrichedRecords || enrichedRecords.length === 0) return [];
    const sample = enrichedRecords[0].data || enrichedRecords[0].Data || enrichedRecords[0];
    return Object.keys(sample)
      .filter(k => typeof sample[k] !== 'object')
      .slice(0, 10)
      .map(k => ({ key: k, label: k }));
  }, [dataType, enrichedRecords, savedColumns]);

  const activeColumns = useMemo(() => {
    if (!enrichedRecords || enrichedRecords.length === 0) return columns;
    return columns.filter(col => {
      return enrichedRecords.some(r => {
        const d = r.data || r.Data || r;
        const v = d[col.key];
        return v !== null && v !== undefined && v !== '';
      });
    });
  }, [columns, enrichedRecords]);

  const sortedRecords = useMemo(() => {
    if (!enrichedRecords || !sortCol) return enrichedRecords || [];
    return [...enrichedRecords].sort((a, b) => {
      const da = a.data || a.Data || a;
      const db = b.data || b.Data || b;
      let va = da[sortCol] ?? '';
      let vb = db[sortCol] ?? '';
      const na = Number(va), nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb)) {
        return sortDir === 'asc' ? na - nb : nb - na;
      }
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [enrichedRecords, sortCol, sortDir]);

  const handleSort = (key) => {
    if (sortCol === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(key);
      setSortDir('asc');
    }
  };

  // The cache occasionally hands us Salesforce date/datetime objects
  // ({ isValidDateTime, year, month, day, ..., value }) instead of an
  // ISO string. Render those as a real date instead of dumping the
  // whole object into the cell as JSON (which is ugly and, when done
  // via {val} directly, also throws React #31).
  const formatValue = (val) => {
    if (val === null || val === undefined) return '\u2014';
    if (typeof val === 'object') {
      if (unwrapDateLike(val) != null) {
        const s = safeDateString(val);
        if (s) return s;
      }
      return JSON.stringify(val);
    }
    return String(val);
  };

  const openConfig = useCallback(() => {
    const currentKeys = new Set(columns.map(c => c.key));
    const labelMap = {};
    columns.forEach(c => { labelMap[c.key] = c.label; });

    // Synthetic enrichment fields don't exist on the raw cached records,
    // so the backend's /drilldown-fields scanner won't return them. Inject
    // them here so the user can toggle them in the column picker.
    const syntheticForType = {
      faxes: ['facility_name', 'recipients_summary', 'recipients_count', 'recipients_read_count', 'recipient_user_ids'],
      'study-details': ['facility_name'],
    };
    const synthetic = syntheticForType[dataType] || [];

    const allFields = new Set([...availableFields, ...currentKeys, ...synthetic]);
    const draft = Array.from(allFields).sort().map(key => ({
      key,
      label: labelMap[key] || key,
      enabled: currentKeys.has(key)
    }));
    setConfigDraft(draft);
    setShowConfig(true);
  }, [columns, availableFields, dataType]);

  const toggleField = (key) => {
    setConfigDraft(prev => prev.map(f =>
      f.key === key ? { ...f, enabled: !f.enabled } : f
    ));
  };

  const updateLabel = (key, label) => {
    setConfigDraft(prev => prev.map(f =>
      f.key === key ? { ...f, label } : f
    ));
  };

  const moveField = (index, direction) => {
    setConfigDraft(prev => {
      const enabledItems = prev.filter(f => f.enabled);
      const disabledItems = prev.filter(f => !f.enabled);
      const enabledIdx = enabledItems.findIndex(f => f.key === prev[index]?.key);
      if (enabledIdx < 0) return prev;
      const newIdx = enabledIdx + direction;
      if (newIdx < 0 || newIdx >= enabledItems.length) return prev;
      const copy = [...enabledItems];
      [copy[enabledIdx], copy[newIdx]] = [copy[newIdx], copy[enabledIdx]];
      return [...copy, ...disabledItems];
    });
  };

  const saveConfig = async () => {
    const selected = configDraft
      .filter(f => f.enabled)
      .map(f => ({ key: f.key, label: f.label }));

    if (selected.length === 0) return;

    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/cache/drilldown-config/${dataType}`, selected);
      setSavedColumns(selected);
      setShowConfig(false);
    } catch (err) {
      console.error('Error saving drilldown config:', err);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    const defaults = DEFAULT_FIELDS[dataType] || [];
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/cache/drilldown-config/${dataType}`, defaults);
      setSavedColumns(null);
      setShowConfig(false);
    } catch (err) {
      console.error('Error resetting drilldown config:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!records || records.length === 0) return null;

  const enabledCount = configDraft.filter(f => f.enabled).length;

  return (
    <div className="drilldown-overlay" onClick={onClose}>
      <div className={`drilldown-modal ${showConfig ? 'drilldown-modal--with-config' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="drilldown-header">
          <div>
            <h3>Drill-Down: {binLabel}</h3>
            <span className="drilldown-count">
              {records.length} record{records.length !== 1 ? 's' : ''}
              {focusField && focusSum != null && (
                <>
                  {' \u00B7 '}
                  {focusSum.toLocaleString()} {focusLabel} total
                  {chartPointValue != null && chartPointValue !== focusSum && (
                    <> (chart: {chartPointValue.toLocaleString()})</>
                  )}
                  {periodRowCount != null && periodRowCount > records.length && (
                    <> · from {periodRowCount.toLocaleString()} daily rollups</>
                  )}
                </>
              )}
              {' \u00B7 '}{activeColumns.length} column{activeColumns.length !== 1 ? 's' : ''}
              {savedColumns ? ' (custom)' : ' (default)'}
              {enrichmentLoading ? ' \u00B7 enriching\u2026' : ''}
            </span>
          </div>
          <div className="drilldown-header-actions">
            <button
              className={`drilldown-config-btn ${showConfig ? 'active' : ''}`}
              onClick={openConfig}
              title="Configure columns"
              disabled={!configLoaded}
            >
              &#9881;
            </button>
            <button className="drilldown-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {showConfig && (
          <div className="drilldown-config-panel">
            <div className="drilldown-config-header">
              <h4>Configure Columns</h4>
              <span className="drilldown-config-count">{enabledCount} selected</span>
            </div>
            <div className="drilldown-config-list">
              {configDraft.filter(f => f.enabled).map((field, idx) => (
                <div key={field.key} className="drilldown-config-item enabled">
                  <input
                    type="checkbox"
                    checked={true}
                    onChange={() => toggleField(field.key)}
                  />
                  <input
                    type="text"
                    className="drilldown-config-label"
                    value={field.label}
                    onChange={(e) => updateLabel(field.key, e.target.value)}
                    title={`Field: ${field.key}`}
                  />
                  <span className="drilldown-config-key">{field.key}</span>
                  <div className="drilldown-config-arrows">
                    <button
                      disabled={idx === 0}
                      onClick={() => moveField(configDraft.indexOf(field), -1)}
                      title="Move up"
                    >&uarr;</button>
                    <button
                      disabled={idx === enabledCount - 1}
                      onClick={() => moveField(configDraft.indexOf(field), 1)}
                      title="Move down"
                    >&darr;</button>
                  </div>
                </div>
              ))}
              {configDraft.some(f => !f.enabled) && (
                <div className="drilldown-config-divider">Available fields</div>
              )}
              {configDraft.filter(f => !f.enabled).map(field => (
                <div key={field.key} className="drilldown-config-item disabled">
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => toggleField(field.key)}
                  />
                  <span className="drilldown-config-item-label">{field.key}</span>
                </div>
              ))}
            </div>
            <div className="drilldown-config-actions">
              <button className="drilldown-config-reset" onClick={resetToDefaults} disabled={saving}>
                Reset to Defaults
              </button>
              <div>
                <button className="drilldown-config-cancel" onClick={() => setShowConfig(false)} disabled={saving}>
                  Cancel
                </button>
                <button className="drilldown-config-save" onClick={saveConfig} disabled={saving || enabledCount === 0}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="drilldown-table-wrap">
          <table className="drilldown-table">
            <thead>
              <tr>
                <th className="drilldown-row-num">#</th>
                {activeColumns.map(col => (
                  <th
                    key={col.key}
                    className={`drilldown-sortable ${sortCol === col.key ? 'sorted' : ''}`}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {sortCol === col.key && (
                      <span className="sort-arrow">{sortDir === 'asc' ? ' \u25B2' : ' \u25BC'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRecords.map((record, idx) => {
                const d = record.data || record.Data || record;
                return (
                  <tr key={record.id || idx}>
                    <td className="drilldown-row-num">{idx + 1}</td>
                    {activeColumns.map(col => (
                      <td key={col.key}>{formatValue(d[col.key])}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DrillDownModal;
